import moment from "moment";
import { formatMoney } from "../../features/utils/formatMoney";
import { getSupplierName } from "../flight/supplier/getSupplierName";
import { getSupplierClass } from "../../components/flight/FlightOfferDisplay";
import { flightStatusMap } from "../../pages/Orders/OrdersData";
import Icon from "../../components/HOC/Icon";
import { Link } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  getChangeStatusName,
} from "../flight/order/getStatusName";
import OrderStatus from "../../components/order/OrderStatus";
import { getFlightFareTypeTagElement } from "./flightFareTypeTag";
import {
  getFlightBookingFromChange,
  getRequestedDateChangeLines,
  getChangeRequestPassengers,
  isRouteChangeRequest,
} from "../flight/order/getRequestedDateChange";

function CopyableTicketNumber({ value }) {
  const { enqueueSnackbar } = useSnackbar();

  function handleCopy(event) {
    event?.stopPropagation?.();
    try {
      navigator.clipboard.writeText(value);
      enqueueSnackbar("Copied to clipboard!", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to copy to clipboard!", { variant: "error" });
    }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="flex gap-1 items-center text-left w-full group"
      title={`Copy ${value}`}
    >
      <span className="block text-xs font-medium text-primary1 break-all">
        {value}
      </span>
      <Icon
        icon="mdi:content-copy"
        className="!w-3.5 !h-3.5 shrink-0 text-gray-400 group-hover:text-primary1"
      />
    </button>
  );
}

function splitTicketParts(value) {
  return String(value || "")
    .split("/")
    .map((part) => part.trim())
    .filter(Boolean);
}

function getPassengerTicketBoxes(data, flightData) {
  const info = Array.isArray(data?.passengerInfo) ? data.passengerInfo : [];
  const travelers = flightData?.travelers || [];
  const scoped = info.length
    ? travelers.filter((traveler) =>
        info.some((p) => String(p?.id) === String(traveler?.id)),
      )
    : travelers;

  const boxes = (scoped.length ? scoped : travelers)
    .map((traveler) => {
      const parts = [
        ...new Set(
          (traveler?.ticketNumbers || []).flatMap((ticket) =>
            splitTicketParts(ticket),
          ),
        ),
      ];
      if (!parts.length) return null;
      return {
        id: traveler?.id,
        name: [traveler?.firstName, traveler?.lastName].filter(Boolean).join(" "),
        parts,
      };
    })
    .filter(Boolean);

  if (boxes.length) return boxes;

  const fallbackParts = [
    ...new Set(
      travelers.flatMap((traveler) =>
        (traveler?.ticketNumbers || []).flatMap((ticket) =>
          splitTicketParts(ticket),
        ),
      ),
    ),
  ];
  return fallbackParts.length ? [{ id: "all", name: "", parts: fallbackParts }] : [];
}

function getRequestedChangeLines(data, flightData) {
  const lines = getRequestedDateChangeLines(data, flightData);
  if (!lines.length) {
    return isRouteChangeRequest(data, flightData)
      ? ["Route change — see request"]
      : ["No new dates listed"];
  }
  return lines.map((item) => item.line);
}

function getTicketNumbersForChange(data, flightData) {
  return getPassengerTicketBoxes(data, flightData).flatMap((box) => box.parts);
}

export function templateFlightOrderReissueData(data) {
  const flightData = getFlightBookingFromChange(data);
  let flightType = "One Way";
  if (flightData?.destinations?.length) {
    flightType = "Multi City";
    if (
      flightData?.destinations?.at(-1)?.to ===
      flightData?.destinations?.at(0)?.from
    ) {
      flightType = "Return";
    }
  }

  const ticketBoxes = getPassengerTicketBoxes(data, flightData);
  const tickets = getTicketNumbersForChange(data, flightData);
  const requestedLines = getRequestedChangeLines(data, flightData);
  const routeChange = isRouteChangeRequest(data, flightData);
  const passengerNames = getChangeRequestPassengers(data, flightData);
  const passengers = passengerNames.join(", ");
  const passengerNote =
    Array.isArray(data?.passengerInfo) && data.passengerInfo.length
      ? data.passengerInfo.length === 1
        ? "1 passenger on this change"
        : `${data.passengerInfo.length} passengers on this change`
      : "";

  return [
    {
      elements: [
        {
          type: "title",
          value: moment(data?.createdAt)?.format("DD-MM-YYYY HH:mm [GMT]Z"),
        },
        {
          type: "copy",
          value: data?.orderId || flightData?.orderId || data?.bookingId,
          renderValue: () => (
            <Link
              target="_blank"
              to={
                "/order/flight/ticket/changedate/confirmation/" +
                data?._id +
                "?page=Date Change"
              }
            >
              {data?.orderId || flightData?.orderId || data?.bookingId || "-"}
            </Link>
          ),
        },
        {
          type: "text",
          value: flightData?.bookingId ? (
            <small className="text-gray-500">Booking {flightData.bookingId}</small>
          ) : (
            ""
          ),
        },
        {
          type: "tag",
          value: getSupplierName(flightData?.supplier),
          className: getSupplierClass(flightData?.supplier),
          color: "text-[#BD002A]",
          background: "bg-[#BD002A]/20",
        },
        getFlightFareTypeTagElement(flightData),
        {
          type: "Agent",
          render: () => (
            <div className="py-2">
              <small>
                {(data?.account?.firstName || "") +
                  " " +
                  (data?.account?.lastName || "")}
              </small>
              <div
                className="w-[200px] overflow-hidden whitespace-nowrap text-ellipsis"
                title={data?.account?.detail?.tradingName}
              >
                {data?.account?.detail?.tradingName}
              </div>
            </div>
          ),
        },
      ],
      value: moment(data?.createdAt)?.format("YYYY-MM-DD HH:mm"),
      width: "md:w-[22%]",
    },
    {
      elements: [
        { type: "title", value: "Requested change" },
        {
          type: "text",
          render: () => (
            <div className="flex flex-col gap-1 text-sm">
              {routeChange ? (
                <span className="inline-flex w-fit rounded-md bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-900">
                  Route change
                </span>
              ) : null}
              {requestedLines.map((line) => (
                <div key={line} className="leading-snug">
                  {line}
                </div>
              ))}
              {routeChange ? (
                <small className="text-amber-800">
                  Compared to original {flightData?.destinations?.map((d) => `${d?.from} - ${d?.to}`).filter(Boolean).join(" / ") || "itinerary"}
                </small>
              ) : null}
              <small className="text-gray-500">
                Submitted {moment(data?.createdAt)?.format("D MMM YYYY HH:mm")}
              </small>
            </div>
          ),
        },
      ],
      value: requestedLines.join(" | "),
      width: "md:w-[18%]",
    },
    {
      elements: [
        { type: "title", value: flightType },
        {
          type: "text",
          render: () => (
            <div>
              {flightData?.destinations
                ?.slice(0, 1)
                ?.map((obj) => `${obj?.from} - ${obj?.to}`)
                ?.join(",") || ""}
              <div>
                <span className="text-theme1">{flightData?.pnr}</span>
              </div>
              <small>{flightData?.flights?.at(0)?.airlineName}</small>
            </div>
          ),
        },
      ],
      width: "md:w-[12%]",
    },
    {
      elements: [
        { type: "title", value: "Ticket" },
        {
          type: "text",
          render: () =>
            ticketBoxes.length ? (
              <div className="flex flex-col gap-2">
                {ticketBoxes.map((box) => (
                  <div
                    key={box.id || box.parts.join("-")}
                    className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5"
                  >
                    {ticketBoxes.length > 1 && box.name ? (
                      <span className="block text-[10px] text-gray-500 mb-1 truncate">
                        {box.name}
                      </span>
                    ) : null}
                    <div className="flex flex-col gap-1">
                      {box.parts.map((ticket) => (
                        <CopyableTicketNumber key={ticket} value={ticket} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <span>-</span>
            ),
        },
        {
          type: "text",
          value: flightData?.status ? (
            <small className="text-gray-500">{flightData.status}</small>
          ) : (
            ""
          ),
        },
      ],
      value: tickets.join(" | "),
      width: "md:w-[12%]",
    },
    {
      elements: [
        { type: "title", value: "GDS PNR" },
        { type: "copy", value: flightData?.pnr },
        { type: "title", value: "Airline PNR" },
        { type: "copy", value: flightData?.pnr },
      ],
      width: "md:w-[12%]",
    },
    {
      elements: [
        { type: "title", value: "Passenger" },
        {
          type: "text",
          value: passengers || "-",
        },
        passengerNote
          ? {
              type: "text",
              value: <small className="text-gray-500">{passengerNote}</small>,
            }
          : { type: "text", value: "" },
      ],
      width: "md:w-[12%]",
    },
    {
      elements: [
        { type: "title", value: "Fee" },
        {
          type: "text",
          value: data?.payable ? formatMoney(data?.payable) : "-",
        },
      ],
      value: data?.payable || flightData?.payable,
      width: "md:w-[10%]",
    },
    {
      elements: [
        { type: "title", value: "Status" },
        {
          type: "status",
          value: getChangeStatusName(flightStatusMap(data?.status), data),
          render: () => {
            const status = flightStatusMap(data?.status);
            const assigned =
              [
                "Approved",
                "Pending",
                "ManualReviewRequired",
                "DateChangeRequestSubmitted",
                "AutoDateChangeFailed",
                "ManualDateChangeProcessing",
              ].includes(status) && data?.tasks?.at(0);
            return (
              <div className="rounded-md mb-3">
                <span
                  className="inline-block overflow-hidden text-ellipsis text-xs"
                  title={status}
                >
                  {assigned ? (
                    ["Approved", "Pending"]?.includes(status) ? (
                      !["ToDo"]?.includes(assigned?.taskStatus) ? (
                        `Awaiting Quote by ${assigned?.assignee?.firstName} ${assigned?.assignee?.lastName}`
                      ) : ["ToDo"]?.includes(assigned?.taskStatus) ? (
                        `Awaiting acceptance by ${assigned?.assignee?.firstName} ${assigned?.assignee?.lastName}`
                      ) : (
                        `Assigned to ${assigned?.assignee?.firstName} ${assigned?.assignee?.lastName}`
                      )
                    ) : (
                      `Under Review by ${assigned?.assignee?.firstName} ${assigned?.assignee?.lastName}`
                    )
                  ) : (
                    <OrderStatus value={getChangeStatusName(status, data)} />
                  )}
                </span>
              </div>
            );
          },
        },
        {
          type: "time",
          render: () => {
            const diffInSeconds = moment
              .utc(flightData?.ticketingExpiry)
              .diff(moment.utc(), "seconds");
            const hours = Math.floor(diffInSeconds / 3600);
            const minutes = Math.floor((diffInSeconds % 3600) / 60);

            return (hours || minutes) > 0 ? (
              <div className="flex gap-1 items-center">
                <Icon icon="mdi:clock-outline" className="!w-4 !h-4" />
                {hours || minutes} {hours ? "Hrs" : "Mins"}
              </div>
            ) : null;
          },
        },
      ],
      value: data?.status || "",
      width: "md:w-[14%]",
    },
  ];
}
